import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Lock } from "lucide-react";
import { api, guestHeaders, getGuestSession, clearGuestSession, photoFileUrl } from "../lib/api";
import { GuestCodeForm } from "./GuestCodeForm";
import { Reveal } from "./Reveal";

export const Gallery = () => {
  const [session, setSession] = useState(getGuestSession());
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    const sync = () => setSession(getGuestSession());
    window.addEventListener("guest-session", sync);
    return () => window.removeEventListener("guest-session", sync);
  }, []);

  useEffect(() => {
    if (!session) return;
    api
      .get("/photos", { headers: guestHeaders() })
      .then((r) => setPhotos(r.data.photos))
      .catch(() => {
        clearGuestSession();
        setSession(null);
      });
  }, [session]);

  return (
    <section id="fotos" data-testid="gallery-section" className="relative py-28 md:py-40 px-6 bg-cream">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <p className="font-script text-clay text-3xl md:text-4xl text-center">Recuerdos</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-center mt-3 text-ink">
            Fotos de la Boda
          </h2>
        </Reveal>

        {!session ? (
          <Reveal
            delay={0.15}
            data-testid="gallery-locked"
            className="max-w-md mx-auto mt-14 border border-clay/15 rounded-3xl bg-sand p-10 text-center"
          >
            <Lock className="mx-auto text-clay" size={28} strokeWidth={1.5} />
            <p className="mt-5 text-stone text-sm leading-relaxed">
              Esta sección es exclusiva para nuestros invitados. Ingresa el código de tu invitación para ver los
              recuerdos del gran día.
            </p>
            <GuestCodeForm onSuccess={setSession} />
          </Reveal>
        ) : photos === null ? (
          <p data-testid="gallery-loading" className="text-center mt-16 text-stone font-serif italic">
            Cargando recuerdos…
          </p>
        ) : photos.length === 0 ? (
          <Reveal
            data-testid="gallery-empty"
            className="max-w-md mx-auto mt-14 border border-clay/15 rounded-3xl bg-sand p-10 text-center"
          >
            <Camera className="mx-auto text-clay" size={28} strokeWidth={1.5} />
            <p className="mt-5 text-stone text-sm leading-relaxed">
              Las fotos del gran día aparecerán aquí después del 10 de octubre. ¡Vuelve pronto!
            </p>
          </Reveal>
        ) : (
          <>
            <div className="text-center mt-10">
              <a
                data-testid="gallery-share-link"
                href="/momentos"
                className="inline-block rounded-full border border-clay text-clay px-8 py-3 text-[11px] uppercase tracking-[0.25em] hover:bg-clay hover:text-cream transition-colors"
              >
                ¿Tomaste fotos? Compártelas aquí
              </a>
            </div>
            <div data-testid="gallery-grid" className="columns-2 md:columns-3 gap-4 mt-10">
              {photos.map((p, i) => (
                <motion.figure
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: (i % 6) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="mb-4 break-inside-avoid"
                >
                  {p.content_type?.startsWith("video/") ? (
                    <video
                      src={photoFileUrl(p.id)}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded-2xl"
                    />
                  ) : (
                    <div className="overflow-hidden rounded-2xl">
                      <img
                        src={photoFileUrl(p.id)}
                        alt={p.original_filename || "Foto de la boda"}
                        loading="lazy"
                        className="w-full hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  {p.uploaded_by && (
                    <figcaption className="mt-2 pl-1 text-[11px] tracking-[0.15em] uppercase text-stone">
                      por {p.uploaded_by}
                    </figcaption>
                  )}
                </motion.figure>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
